import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFileArchive, getFiles } from 'services/files';
import { useDebouncedCallback } from 'use-debounce';
import { StatusFileFilter } from 'const/common';
import { Files } from 'services/leads';
import { createUserCloseFile } from 'services/userCloseFile';
import { useFlash } from 'contexts/flash';
import { useAsyncCallback } from 'hooks/useAsyncCallback';

export interface FilterFormValues {
  earlyMorning?: boolean;
  doorKnock?: boolean;
  status: string[];
  types: string[];
  page?: number;
  size?: number;
  searchText?: string;
}
const size = 25;

const initialFilters = {
  searchText: '',
  earlyMorning: undefined,
  doorKnock: undefined,
  status: [],
  types: [],
  sortBy: undefined,
  order: undefined
};

export const useHomePage = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    openFiles: {
      searchText?: string;
      earlyMorning?: boolean;
      doorKnock?: boolean;
      status: string[];
      types: string[];
      sortBy?: string;
      order?: string;
    };
    onHold: {
      searchText?: string;
      earlyMorning?: boolean;
      doorKnock?: boolean;
      status: string[];
      types: string[];
      sortBy?: string;
      order?: string;
    };
  }>({
    openFiles: { ...initialFilters },
    onHold: { ...initialFilters }
  });

  const [currentStatus, setCurrentStatus] = useState<string>(StatusFileFilter.OPEN_FILE);
  const [openFilesData, setOpenFilesData] = useState<{
    rows: Files[];
    page: number;
    hasMore: boolean;
  }>({
    rows: [],
    page: 1,
    hasMore: false
  });

  const [onHoldData, setOnHoldData] = useState<{ rows: Files[]; page: number; hasMore: boolean }>({
    rows: [],
    page: 1,
    hasMore: false
  });

  const [closeFileData, setCloseFileData] = useState<{
    rows: { id: number; status: string; previousStatus?: string; file: Files }[];
    page: number;
    hasMore: boolean;
  }>({
    rows: [],
    page: 1,
    hasMore: false
  });

  const [fileCloseDelete, setFileCloseDelete] = useState<{
    id: number;
    status: string;
    previousStatus?: string;
    file: Files;
  } | null>(null);

  const fetchCloseFileData = useCallback(async (page = 1) => {
    const params = {
      page,
      size,
      sortBy: 'createdAt',
      order: 'desc'
    };
    const data = await getFileArchive(params);
    setCloseFileData((prev) => ({
      rows: page === 1 ? data?.archives : [...prev.rows, ...(data?.archives || [])],
      page: page + 1,
      hasMore: data?.total > size * page
    }));
  }, []);

  const { asyncCallback: asyncFetchCloseFileData } = useAsyncCallback(fetchCloseFileData, []);

  const fetchMoreCloseFileData = useCallback(async () => {
    if (!closeFileData) return;
    await fetchCloseFileData(closeFileData.page);
  }, [closeFileData, fetchCloseFileData]);

  useEffect(() => {
    asyncFetchCloseFileData();
  }, [asyncFetchCloseFileData]);

  const fetchOpenFileData = useCallback(
    async (page = 1) => {
      const params = {
        ...filters.openFiles,
        fileStatus: StatusFileFilter.OPEN_FILE,
        page,
        size
      };
      const data = await getFiles(params);

      setOpenFilesData((prev) => ({
        rows: page === 1 ? data.files : [...prev.rows, ...data.files],
        page: page + 1,
        hasMore: data.total > size * page
      }));
    },
    [filters.openFiles]
  );
  const { asyncCallback: asyncFetchOpenFileData } = useAsyncCallback(fetchOpenFileData, []);

  const fetchOnHoldData = useCallback(
    async (page = 1) => {
      const params = {
        ...filters.onHold,
        fileStatus: StatusFileFilter.ON_HOLD,
        page,
        size
      };
      const data = await getFiles(params);

      setOnHoldData((prev) => ({
        rows: page === 1 ? data.files : [...prev.rows, ...data.files],
        page: page + 1,
        hasMore: data.total > size * page
      }));
    },
    [filters.onHold]
  );
  const { asyncCallback: asyncFetchOnHoldData } = useAsyncCallback(fetchOnHoldData, []);

  useEffect(() => {
    asyncFetchOpenFileData();
  }, [fetchOpenFileData]);

  useEffect(() => {
    asyncFetchOnHoldData();
  }, [fetchOnHoldData]);

  const fetchMoreOpenFileData = useCallback(async () => {
    if (!openFilesData) return;
    await fetchOpenFileData(openFilesData.page);
  }, [openFilesData, fetchOpenFileData]);

  const fetchMoreOnHoldData = useCallback(async () => {
    if (!onHoldData) return;
    await fetchOnHoldData(onHoldData.page);
  }, [onHoldData, fetchOnHoldData]);

  const handleInputSearch = useDebouncedCallback((e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (currentStatus === StatusFileFilter.OPEN_FILE) {
      setFilters((prev) => ({
        ...prev,
        openFiles: {
          ...prev.openFiles,
          searchText: inputValue || ''
        }
      }));
      setOpenFilesData({ rows: [], page: 1, hasMore: false });
    }

    if (currentStatus === StatusFileFilter.ON_HOLD) {
      setFilters((prev) => ({
        ...prev,
        onHold: {
          ...prev.onHold,
          searchText: inputValue || ''
        }
      }));
      setOnHoldData({ rows: [], page: 1, hasMore: false });
    }
  }, 500);

  const handleStatusChange = useCallback((status: string) => {
    setCurrentStatus(status);
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCancel = () => {
    setSearch(false);

    if (currentStatus === StatusFileFilter.OPEN_FILE) {
      if (!filters.openFiles.searchText) return;
      setFilters((prev) => ({
        ...prev,
        openFiles: {
          ...prev.openFiles,
          searchText: ''
        }
      }));
      setOpenFilesData({ rows: [], page: 1, hasMore: false });
    }

    if (currentStatus === StatusFileFilter.ON_HOLD) {
      if (!filters.onHold.searchText) return;
      setFilters((prev) => ({
        ...prev,
        onHold: {
          ...prev.onHold,
          searchText: ''
        }
      }));
      setOnHoldData({ rows: [], page: 1, hasMore: false });
    }
  };

  const handleClickRow = useCallback(
    (indexRow: number) => {
      const rows =
        currentStatus === StatusFileFilter.OPEN_FILE ? openFilesData.rows : onHoldData.rows;
      const id = rows.find((_, index) => index === indexRow)?.id;
      if (id) {
        navigate(`/file/${id}`);
      }
    },
    [currentStatus, openFilesData.rows, onHoldData.rows, navigate]
  );
  const onFilter = useCallback(
    (valueForm: FilterFormValues) => {
      if (currentStatus === StatusFileFilter.OPEN_FILE) {
        setFilters((prev) => ({
          ...prev,
          openFiles: {
            ...prev.openFiles,
            searchText: valueForm.searchText || '',
            earlyMorning: valueForm.earlyMorning,
            doorKnock: valueForm.doorKnock,
            status: valueForm.status || [],
            types: valueForm.types || [],
            sortBy: undefined,
            order: undefined
          }
        }));
        setOpenFilesData({ rows: [], page: 1, hasMore: false });
      }

      if (currentStatus === StatusFileFilter.ON_HOLD) {
        setFilters((prev) => ({
          ...prev,
          onHold: {
            ...prev.onHold,
            searchText: valueForm.searchText || '',
            earlyMorning: valueForm.earlyMorning,
            doorKnock: valueForm.doorKnock,
            status: valueForm.status || [],
            types: valueForm.types || [],
            sortBy: undefined,
            order: undefined
          }
        }));
        setOnHoldData({ rows: [], page: 1, hasMore: false });
      }
    },
    [currentStatus]
  );

  const handleResetFilter = useCallback(() => {
    if (currentStatus === StatusFileFilter.OPEN_FILE) {
      setFilters((prev) => ({
        ...prev,
        openFiles: { ...initialFilters }
      }));
      setOpenFilesData({ rows: [], page: 1, hasMore: false });
    }

    if (currentStatus === StatusFileFilter.ON_HOLD) {
      setFilters((prev) => ({
        ...prev,
        onHold: { ...initialFilters }
      }));
      setOnHoldData({ rows: [], page: 1, hasMore: false });
    }
  }, [currentStatus]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      if (currentStatus === StatusFileFilter.OPEN_FILE) {
        setFilters((prev) => {
          let order = 'asc';
          if (prev.openFiles.sortBy === sortField) {
            order = prev.openFiles.order === 'asc' ? 'desc' : 'asc';
          }
          return {
            ...prev,
            openFiles: {
              ...prev.openFiles,
              sortBy: sortField,
              order
            }
          };
        });
        setOpenFilesData({ rows: [], page: 1, hasMore: false });
      }

      if (currentStatus === StatusFileFilter.ON_HOLD) {
        setFilters((prev) => {
          let order = 'asc';
          if (prev.onHold.sortBy === sortField) {
            order = prev.onHold.order === 'asc' ? 'desc' : 'asc';
          }
          return {
            ...prev,
            onHold: {
              ...prev.onHold,
              sortBy: sortField,
              order
            }
          };
        });
        setOnHoldData({ rows: [], page: 1, hasMore: false });
      }
    },
    [currentStatus]
  );

  const rows = currentStatus === StatusFileFilter.OPEN_FILE ? openFilesData.rows : onHoldData.rows;

  const hasMore =
    currentStatus === StatusFileFilter.OPEN_FILE ? openFilesData.hasMore : onHoldData.hasMore;

  const searchInput =
    currentStatus === StatusFileFilter.OPEN_FILE
      ? filters.openFiles.searchText || ''
      : filters.onHold.searchText || '';

  const formValue =
    currentStatus === StatusFileFilter.OPEN_FILE ? filters.openFiles : filters.onHold;
  const { setFlash } = useFlash();
  const [reason, setReason] = useState<string>('');
  const onDeleteCloseFile = async () => {
    if (!fileCloseDelete?.file?.id) return;
    await createUserCloseFile(Number(fileCloseDelete.file.id), reason);
    await fetchCloseFileData();
    setFlash({ type: 'success', message: 'Delete closed file successfully' });
    setFileCloseDelete(null);
    setReason('');
  };

  return {
    open,
    search,
    searchInput,
    selectedStatus: currentStatus,
    rows,
    setSearch,
    handleOpen,
    handleClose,
    handleCancel,
    handleInputSearch,
    handleStatusChange,
    handleClickRow,
    onFilter,
    handleResetFilter,
    formValue,
    fetchMoreData:
      currentStatus === StatusFileFilter.OPEN_FILE ? fetchMoreOpenFileData : fetchMoreOnHoldData,
    hasMore,
    onSortHeader,
    closeFileData,
    fetchMoreCloseFileData,
    onDeleteCloseFile,
    setFileCloseDelete,
    fileCloseDelete,
    reason,
    setReason
  };
};
